<template>
  <list-page-shell>
    <template #actions>
      <el-button type="primary" size="small" icon="el-icon-plus" @click="openCustomerForm(null)">{{ $t('customerListPage.create') }}</el-button>
    </template>

    <template #filters>
      <el-form :inline="true" :model="filters" size="small">
        <el-form-item :label="$t('customerListPage.keywordLabel')">
          <el-input
            v-model="filters.keyword"
            clearable
            :placeholder="$t('customerListPage.keywordPh')"
            style="width:240px;"
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" :loading="loading" @click="handleSearch">{{ $t('common.search') }}</el-button>
          <el-button icon="el-icon-refresh" :disabled="loading" @click="handleReset">{{ $t('common.reset') }}</el-button>
        </el-form-item>
      </el-form>
    </template>

    <el-table :data="list" border stripe v-loading="loading" class="customer-table" @row-click="row => openCustomerForm(row.id)">
      <el-table-column prop="customerNo" :label="$t('grid.customerNo')" min-width="130" />
      <el-table-column :label="$t('grid.contact')" min-width="140" show-overflow-tooltip>
        <template slot-scope="s">{{ contactLabel(s.row) }}</template>
      </el-table-column>
      <el-table-column prop="phone" :label="$t('grid.phone')" width="130" />
      <el-table-column prop="email" :label="$t('grid.email')" min-width="160" show-overflow-tooltip />
      <el-table-column v-if="canAssignSales" :label="$t('customerListPage.salesOwner')" min-width="130" show-overflow-tooltip>
        <template slot-scope="s">{{ s.row.assignedSalesUserName || '—' }}</template>
      </el-table-column>
      <el-table-column :label="$t('common.status')" width="88">
        <template slot-scope="s">{{ labelCustomerStatus(s.row.statusCode) }}</template>
      </el-table-column>
      <el-table-column :label="$t('grid.createdAt')" min-width="200">
        <template slot-scope="s">{{ formatDisplayDateTime(s.row.createdAt) || '—' }}</template>
      </el-table-column>
      <el-table-column :label="$t('common.actions')" width="100" fixed="right">
        <template slot-scope="s">
          <el-button type="text" size="small" @click.stop="openCustomerForm(s.row.id)">{{ $t('common.edit') }}</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="list-pager">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next"
        :total="total"
        :page-sizes="[10, 20, 50]"
        :page-size="pageSize"
        :current-page="pageNo"
        @size-change="handleSizeChange"
        @current-change="p => { pageNo = p; fetchList() }"
      />
    </div>

    <RecordFormDialog :visible.sync="customerFormVisible" :title="customerFormTitle">
      <CustomerForm
        v-if="customerFormVisible"
        ref="customerFormRef"
        :key="customerFormKey"
        embedded
        :record-id="customerFormRecordId"
        @saved="onCustomerFormSaved"
        @saving="v => (customerFormSaving = v)"
      />
      <template slot="footer">
        <el-button @click="customerFormVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="customerFormSaving" @click="submitCustomerFormDialog">{{ $t('common.save') }}</el-button>
      </template>
    </RecordFormDialog>
  </list-page-shell>
</template>

<script>
import ListPageShell from '@/components/ListPageShell.vue'
import RecordFormDialog from '@/components/RecordFormDialog.vue'
import CustomerForm from '@/views/customer/CustomerForm.vue'
import { getCustomerPage } from '@/api/customer'
import { labelCustomerStatus } from '@/utils/crmLabels'
import { formatDisplayDateTime } from '@/utils/formatDisplayDateTime'

export default {
  name: 'CustomerList',
  components: { ListPageShell, RecordFormDialog, CustomerForm },
  data() {
    return {
      customerFormVisible: false,
      customerFormSaving: false,
      customerFormRecordId: null,
      customerFormKey: 0,
      list: [],
      total: 0,
      pageNo: 1,
      pageSize: 10,
      loading: false,
      filters: { keyword: '' }
    }
  },
  computed: {
    customerFormTitle() {
      return this.customerFormRecordId
        ? this.$t('customerFormPage.editTitle')
        : this.$t('customerFormPage.newTitle')
    },
    canAssignSales() {
      const roles = (this.$store.state.userInfo && this.$store.state.userInfo.roleCodes) || []
      return roles.includes('OFFICE') || roles.includes('ADMIN')
    }
  },
  created() {
    this.fetchList()
  },
  methods: {
    labelCustomerStatus,
    formatDisplayDateTime,
    contactLabel(row) {
      if (!row) return '—'
      const a = [row.firstName, row.lastName].filter(Boolean).join(' ').trim()
      return a || '—'
    },
    handleSearch() {
      this.pageNo = 1
      this.fetchList()
    },
    handleReset() {
      this.filters = { keyword: '' }
      this.pageNo = 1
      this.fetchList()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.pageNo = 1
      this.fetchList()
    },
    async fetchList() {
      this.loading = true
      try {
        const kw = (this.filters.keyword || '').trim()
        const r = await getCustomerPage({
          pageNo: this.pageNo,
          pageSize: this.pageSize,
          keyword: kw || undefined
        })
        const d = r.data !== undefined ? r.data : r
        this.list = d.records || []
        this.total = d.total || 0
      } catch (e) {
        this.list = []
        this.total = 0
        this.$message.error((e && e.message) || this.$t('common.loadFailed'))
      } finally {
        this.loading = false
      }
    },
    openCustomerForm(id) {
      if (id == null) {
        this.customerFormRecordId = null
      } else {
        this.customerFormRecordId = id
      }
      this.customerFormKey += 1
      this.customerFormVisible = true
    },
    onCustomerFormSaved() {
      this.customerFormVisible = false
      this.fetchList()
    },
    submitCustomerFormDialog() {
      if (this.$refs.customerFormRef) this.$refs.customerFormRef.submit()
    }
  }
}
</script>

<style scoped>
.customer-table {
  cursor: pointer;
}

.list-pager {
  margin-top: 16px;
  text-align: right;
}
</style>
